import { createFileRoute } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";

import { KeyRound } from "lucide-react";

import { MfaCard } from "@/components/settings/MfaCard";
import { PasswordChangeCard } from "@/components/settings/PasswordChangeCard";
import { ProfileCard } from "@/components/settings/ProfileCard";
import { SessionsCard } from "@/components/settings/SessionsCard";
import { SystemAdminCard } from "@/components/settings/SystemAdminCard";
import { useCurrentUser } from "@/lib/hooks/auth";
import { useCurrentSession } from "@/lib/session";

export const Route = createFileRoute("/_app/settings")({
  component: SettingsPage,
});

function SettingsPage(): JSX.Element {
  const { t } = useTranslation();
  const sessionQuery = useCurrentSession();
  const userQuery = useCurrentUser();

  return (
    <div className="space-y-4">
      <header>
        <h1 className="text-3xl font-semibold tracking-tight">
          {t("nav.settings")}
        </h1>
        <p className="text-sm text-muted-foreground">
          {t("settings.subtitle")}
        </p>
      </header>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <ProfileCard
          session={sessionQuery.data}
          isLoading={sessionQuery.isLoading}
        />
        <MfaCard mfaEnrolled={userQuery.data?.mfa_enrolled ?? false} />
        {userQuery.data?.is_external ? (
          <ExternalAuthCard />
        ) : (
          <PasswordChangeCard userId={userQuery.data?.id} />
        )}
        <SessionsCard />
        <SystemAdminCard />
      </div>
    </div>
  );
}

/**
 * Shown instead of PasswordChangeCard for externally-authenticated users
 * (external_subject_id set → they log in via Keycloak/Google, not via the
 * DPanel-local password). The local Argon2id password change would not affect
 * their actual login, so we hide it and point them to their IdP. (ADR-0100:
 * a future per-federation distinction could let LOCAL-realm users change their
 * Keycloak password here via the admin API — see backlog 3.11.)
 */
function ExternalAuthCard(): JSX.Element {
  const { t } = useTranslation();
  return (
    <section className="space-y-2 rounded-md border border-border bg-card p-4">
      <header className="flex items-center gap-2">
        <KeyRound className="size-4 text-muted-foreground" aria-hidden="true" />
        <h2 className="text-lg font-semibold">{t("settings.external_auth.title")}</h2>
      </header>
      <p className="text-sm text-muted-foreground">{t("settings.external_auth.body")}</p>
    </section>
  );
}
